import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth, SignedIn, SignedOut, RedirectToSignIn } from "@clerk/clerk-react";
import { apiGet, apiPost } from "../lib/api.js";
import { AdminTabs } from "./_AdminTabs.js";

interface SourceRow {
  id: string;
  url: string;
  vendor_id: string | null;
  status: string;
  error_count: number;
  last_error: string | null;
  last_attempt_at_utc_ms: number | null;
  last_crawled_at_utc_ms: number | null;
}

interface AgentEvent {
  id: string;
  step: string;
  span_id: string;
  attempt: number;
  status: string;
  model: string | null;
  prompt_ver: string | null;
  tokens_in: number | null;
  tokens_out: number | null;
  cost_cents: number | null;
  cost_micros: number | null;
  latency_ms: number | null;
  qa_score: number | null;
  error: string | null;
  output_summary: string | null;
  created_at_utc_ms: number;
}

interface VendorDetail {
  id: string;
  name: string;
  category: string;
  description: string | null;
  website: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  quality_score: number | null;
  rating: number | null;
  review_count: number | null;
  rating_updated_at_utc_ms: number | null;
  status: string;
  source: string;
  ai_generated: boolean;
  categories: Array<{ slug: string; is_primary: boolean; confidence: number | null }>;
  location: {
    address: string;
    city: string | null;
    region: string;
    country: string;
    lat: number | null;
    lng: number | null;
  } | null;
  items: Array<{ id: string; title: string; description: string | null; category: string; price_cents: number | null; currency: string; flower_material: "fresh" | "dried" | "artificial" | null; attributes: Record<string, unknown> | null }>;
  images: Array<{
    id: string;
    r2_key: string;
    source_url: string | null;
    matched_category: string | null;
    quality_score: number | null;
    relevance_score: number | null;
    image_type: string | null;
    /** Linked vendor_item id (null = vendor-level portfolio shot). */
    vendor_item_id: string | null;
    item_title: string | null;
    item_price_cents: number | null;
    item_currency: string | null;
    /** RAG-grade description from image_qa v7. */
    description_long: string | null;
    /** Lowercase flower species (florists only). */
    flower_species: string[] | null;
    flower_palette: string[] | null;
  }>;
}

interface TraceResponse {
  source: SourceRow;
  events: AgentEvent[];
  vendor: VendorDetail | null;
}

const API_BASE = (): string =>
  (window as unknown as { __BRIDEPATH_API__?: string }).__BRIDEPATH_API__ ?? "";

const TERMINAL_STATUSES = new Set([
  "crawled",
  "failed",
  "disabled",
  "demoted",
  "skipped_robots",
]);

export function AdminCrawl() {
  return (
    <>
      <SignedOut><RedirectToSignIn /></SignedOut>
      <SignedIn><AdminCrawlInner /></SignedIn>
    </>
  );
}

function AdminCrawlInner() {
  const { traceId } = useParams<{ traceId?: string }>();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [forceFullRescan, setForceFullRescan] = useState(false);
  const [data, setData] = useState<TraceResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Reset on traceId change.
  useEffect(() => { setData(null); setError(null); }, [traceId]);

  // Poll loop.
  useEffect(() => {
    if (!traceId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const poll = async () => {
      const token = await getToken();
      if (!token || cancelled) return;
      const res = await apiGet<TraceResponse>(`/admin/crawl/${traceId}`, token);
      if (cancelled) return;
      if (res.ok) {
        setData(res.value);
        const s = res.value.source.status;
        if (!TERMINAL_STATUSES.has(s)) {
          timer = setTimeout(poll, 2_000);
        }
      } else {
        setError(JSON.stringify(res.error));
        timer = setTimeout(poll, 5_000);
      }
    };
    void poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [traceId, getToken]);

  const scan = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url.trim() || busy) return;
    setBusy(true);
    setError(null);
    const token = await getToken();
    if (!token) { setBusy(false); return; }
    const res = await apiPost<{ trace_id: string }>(
      "/admin/crawl",
      token,
      { url: url.trim(), force_full_rescan: forceFullRescan },
    );
    setBusy(false);
    if (!res.ok) {
      setError(JSON.stringify(res.error));
      return;
    }
    navigate(`/admin/crawl/${res.value.trace_id}`);
  }, [url, busy, getToken, navigate, forceFullRescan]);

  const reset = useCallback(async () => {
    if (!traceId) return;
    const token = await getToken();
    if (!token) return;
    await apiPost(`/admin/crawl/${traceId}/vendor`, token, {}).catch(() => null);
    // The POST helper above is a hack — we need a DELETE. Fall back to fetch:
    await fetch(`${API_BASE()}/admin/crawl/${traceId}/vendor`, {
      method: "DELETE",
      headers: { authorization: `Bearer ${token}` },
    }).catch(() => null);
    navigate("/admin/crawl");
    setUrl("");
  }, [traceId, getToken, navigate]);

  return (
    <section className="max-w-5xl mx-auto px-6 py-8">
      <AdminTabs />
      <header className="mb-6">
        <h2 className="font-display text-3xl">Debug crawl</h2>
        <p className="mt-1 text-sm text-stone-600">
          Type a vendor's website URL. The pipeline runs against just that URL (no discovery,
          no blacklist) and every step is logged below in real time.
        </p>
      </header>

      <form onSubmit={scan} className="mb-3 flex gap-2">
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          disabled={busy || (data !== null && !TERMINAL_STATUSES.has(data.source.status))}
          placeholder="https://example-vendor.com"
          className="flex-1 px-4 py-2 rounded-full border border-stone-300 focus:outline-none focus:ring-2 focus:ring-stone-900 font-mono text-sm"
        />
        <button
          disabled={busy || !url.trim() || (data !== null && !TERMINAL_STATUSES.has(data.source.status))}
          className="px-5 py-2 rounded-full bg-stone-900 text-stone-50 hover:bg-stone-700 disabled:opacity-40"
        >
          {busy ? "Starting…" : "Scan"}
        </button>
      </form>

      <label className="mb-8 flex items-center gap-2 text-xs text-stone-600 cursor-pointer">
        <input
          type="checkbox"
          checked={forceFullRescan}
          onChange={(e) => setForceFullRescan(e.target.checked)}
          className="rounded border-stone-300"
        />
        <span>
          Re-evaluate every image (skip pHash cache).{" "}
          <span className="text-stone-500">
            Default: skip vision when an image's pHash matches a prior approved image for this vendor.
          </span>
        </span>
      </label>

      {error && (
        <pre className="mb-6 p-3 bg-red-50 text-red-900 rounded-lg font-mono text-xs whitespace-pre-wrap">
          {error}
        </pre>
      )}

      {data && <SourcePanel source={data.source} events={data.events} onReset={reset} />}
      {data && <PartialBanner events={data.events} vendor={data.vendor} />}
      {data && <LogPanel events={data.events} />}
      {data?.vendor && <VendorPanel vendor={data.vendor} events={data.events} />}
    </section>
  );
}

// --- formatting -------------------------------------------------------------

/** µ = 1/10,000 of a cent. Format as cents with 5 decimal places. */
const formatCost = (e: AgentEvent): string => {
  if (e.cost_micros != null) {
    const cents = e.cost_micros / 10_000;
    if (cents < 0.00001) return "0¢";
    return `${cents.toFixed(5)}¢`;
  }
  if (e.cost_cents != null) return `${e.cost_cents.toFixed(2)}¢ (rounded)`;
  return "—";
};

const totalCostMicros = (events: AgentEvent[]): number =>
  events.reduce((s, e) => s + (e.cost_micros ?? (e.cost_cents ?? 0) * 10_000), 0);

const parseSummary = (s: string | null): Record<string, unknown> | null => {
  if (!s) return null;
  try { return JSON.parse(s) as Record<string, unknown>; } catch { return null; }
};

const formatRelativeAge = (utcMs: number): string => {
  const diffMs = Math.max(0, Date.now() - utcMs);
  const hours = Math.round(diffMs / 3_600_000);
  if (hours < 1) return "just now";
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
};

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-stone-500">{label}</div>
      <div className="text-2xl font-display">{value}</div>
    </div>
  );
}

// --- subpanels --------------------------------------------------------------

function SourcePanel({ source, events, onReset }: { source: SourceRow; events: AgentEvent[]; onReset: () => void }) {
  const totalMicros = totalCostMicros(events);
  const totalCostCents = totalMicros / 10_000;
  const totalLatency = events.reduce((s, e) => s + (e.latency_ms ?? 0), 0);
  const terminal = TERMINAL_STATUSES.has(source.status);
  return (
    <div className="mb-6 p-4 border border-stone-200 rounded-lg bg-stone-50 flex items-center justify-between">
      <div>
        <div className="text-xs uppercase tracking-wide text-stone-500">trace</div>
        <div className="font-mono text-sm">{source.id}</div>
        <div className="mt-1 text-sm text-stone-600">{source.url}</div>
        <div className="mt-2 flex gap-3 text-xs text-stone-600">
          <StatusPill status={source.status} />
          <span>cost: <strong>{totalCostCents.toFixed(5)}¢</strong> (${(totalCostCents / 100).toFixed(7)})</span>
          <span>latency: <strong>{(totalLatency / 1000).toFixed(1)}s</strong></span>
          {source.error_count > 0 && <span className="text-red-700">errors: {source.error_count}</span>}
        </div>
        {source.last_error && (
          <div className="mt-2 text-xs text-red-700 font-mono">{source.last_error}</div>
        )}
      </div>
      {terminal && (
        <button
          onClick={onReset}
          className="px-3 py-1.5 rounded-full border border-stone-400 text-xs hover:bg-stone-100"
        >
          Reset (soft-delete + clear cache)
        </button>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const cls =
    status === "crawled" ? "bg-emerald-100 text-emerald-800" :
    status === "failed" || status === "disabled" ? "bg-red-100 text-red-800" :
    status === "demoted" || status === "skipped_robots" ? "bg-amber-100 text-amber-800" :
    "bg-stone-200 text-stone-700";
  return <span className={`px-2 py-0.5 rounded-full ${cls}`}>{status}</span>;
}

function StepPill({ status }: { status: string }) {
  const cls =
    status === "ok" ? "bg-emerald-100 text-emerald-800" :
    status === "fail" ? "bg-red-100 text-red-800" :
    status === "skip" ? "bg-stone-200 text-stone-600" :
    status === "partial" ? "bg-orange-100 text-orange-800" :
    "bg-amber-100 text-amber-800";
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium uppercase ${cls}`}>{status}</span>;
}

function PartialBanner({ events, vendor }: { events: AgentEvent[]; vendor: VendorDetail | null }) {
  // A "partial" outcome surfaces from the store event's output_summary OR
  // the verify_gate event status='partial'. Show what verify said was missing.
  const storeEv = events.find((e) => e.step === "store");
  const storeSummary = storeEv ? parseSummary(storeEv.output_summary) : null;
  const isPartial = storeSummary?.partial === true;
  if (!isPartial) return null;
  const missing = Array.isArray(storeSummary?.missing_reasons) ? storeSummary!.missing_reasons as string[] : [];
  const qs = typeof storeSummary?.quality_score === "number" ? storeSummary!.quality_score as number : null;
  return (
    <div className="mb-4 p-4 rounded-lg bg-orange-50 border border-orange-200 text-sm text-orange-900">
      <strong>Stored as partial.</strong>{" "}
      The vendor was created with the info we could extract, but verify flagged it incomplete.
      {qs != null && <> Quality score: <strong>{(qs * 100).toFixed(0)}</strong>.</>}{" "}
      The next crawl can fill the gaps if more pages turn them up.
      {missing.length > 0 && (
        <ul className="mt-2 text-xs list-disc list-inside text-orange-800">
          {missing.map((m, i) => <li key={i}>{m}</li>)}
        </ul>
      )}
      {vendor && (
        <div className="mt-2 text-xs text-orange-700">
          vendor id: <span className="font-mono">{vendor.id}</span>
        </div>
      )}
    </div>
  );
}

function LogPanel({ events }: { events: AgentEvent[] }) {
  const [open, setOpen] = useState<Set<string>>(new Set());
  if (events.length === 0) {
    return <div className="mb-6 text-sm text-stone-500 italic">waiting for first agent_event…</div>;
  }
  const toggle = (id: string) => {
    setOpen((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  return (
    <div className="mb-8">
      <h3 className="text-xs uppercase tracking-wide text-stone-500 mb-2">agent_event log ({events.length})</h3>
      <div className="overflow-x-auto rounded-lg border border-stone-200">
        <table className="w-full text-xs">
          <thead className="bg-stone-100 text-stone-600">
            <tr>
              <th className="text-left px-2 py-2 font-medium w-6"></th>
              <th className="text-left px-3 py-2 font-medium">t</th>
              <th className="text-left px-3 py-2 font-medium">step</th>
              <th className="text-left px-3 py-2 font-medium">status</th>
              <th className="text-left px-3 py-2 font-medium">model</th>
              <th className="text-right px-3 py-2 font-medium">in/out</th>
              <th className="text-right px-3 py-2 font-medium">cost</th>
              <th className="text-right px-3 py-2 font-medium">ms</th>
              <th className="text-right px-3 py-2 font-medium">qa</th>
              <th className="text-left px-3 py-2 font-medium">error</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {events.map((e, i) => {
              const dt = i === 0 ? 0 : e.created_at_utc_ms - events[0]!.created_at_utc_ms;
              const summary = parseSummary(e.output_summary);
              const expandable = summary !== null;
              const isOpen = open.has(e.id);
              return (
                <>
                  <tr
                    key={e.id}
                    className={`border-t border-stone-100 ${expandable ? "cursor-pointer hover:bg-stone-50" : ""}`}
                    onClick={() => expandable && toggle(e.id)}
                  >
                    <td className="px-2 py-1.5 text-stone-400 text-center">
                      {expandable ? (isOpen ? "▾" : "▸") : ""}
                    </td>
                    <td className="px-3 py-1.5 text-stone-500">+{(dt / 1000).toFixed(1)}s</td>
                    <td className="px-3 py-1.5 font-semibold text-stone-800">{e.step}</td>
                    <td className="px-3 py-1.5"><StepPill status={e.status} /></td>
                    <td className="px-3 py-1.5 text-stone-600">{e.model ?? "—"}</td>
                    <td className="px-3 py-1.5 text-right text-stone-600">
                      {e.tokens_in ?? "—"}/{e.tokens_out ?? "—"}
                    </td>
                    <td className="px-3 py-1.5 text-right text-stone-600">{formatCost(e)}</td>
                    <td className="px-3 py-1.5 text-right text-stone-600">{e.latency_ms ?? "—"}</td>
                    <td className="px-3 py-1.5 text-right text-stone-600">
                      {e.qa_score != null ? (e.qa_score * 100).toFixed(0) : "—"}
                    </td>
                    <td className="px-3 py-1.5 text-stone-500 max-w-[260px] truncate" title={e.error ?? ""}>
                      {e.error ?? <span className="text-stone-400">—</span>}
                    </td>
                  </tr>
                  {isOpen && summary && (
                    <tr key={`${e.id}-details`} className="bg-stone-50">
                      <td></td>
                      <td colSpan={9} className="px-3 py-3">
                        <DetailsTable summary={summary} step={e.step} />
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DetailsTable({ summary, step }: { summary: Record<string, unknown>; step: string }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 font-mono text-[11px]">
      {Object.entries(summary).map(([k, v]) => {
        // Highlight extract.missing[] in amber so the operator immediately
        // sees what the extractor didn't find on the pages we crawled.
        const isMissing = step === "extract" && k === "missing" && Array.isArray(v) && v.length > 0;
        const rendered = typeof v === "object" ? JSON.stringify(v) : String(v);
        return (
          <div key={k} className="flex gap-2">
            <span className="text-stone-500 shrink-0">{k}:</span>
            <span className={`whitespace-pre-wrap break-all ${isMissing ? "text-amber-800 font-semibold" : "text-stone-800"}`}>
              {rendered}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// Per-item saved metadata (vendor_item.attributes JSON) — the sub-classify +
// enrichment output. Rendered generically so any pack's attributes show
// without a per-category branch (venue capacity/sqft/seasonal/dependency,
// photography package inclusions, florals flower_material, …).
const ATTR_LABELS: Record<string, string> = {
  offering_type: "offering",
  space_style: "style",
  square_feet: "sq ft",
  capacity_seated_max: "seated",
  capacity_standing_max: "standing",
  capacity_ceremony_max: "ceremony",
  indoor_outdoor: "indoor/outdoor",
  availability_seasonal: "seasonal",
  available_months: "months",
  availability_notes: "availability",
  requires_other_space: "requires another space",
  dependency_note: "dependency",
  in_house_catering: "in-house catering",
  catering_partner: "caterer",
  included_staff: "staff",
};
const prettyAttrKey = (k: string): string =>
  ATTR_LABELS[k] ?? k.replace(/_/g, " ");
const formatAttrValue = (v: unknown): string => {
  if (Array.isArray(v)) return v.map((x) => String(x)).join(", ");
  if (typeof v === "boolean") return v ? "yes" : "no";
  return String(v);
};

function ItemAttributes({ attributes }: { attributes: Record<string, unknown> | null }) {
  if (!attributes || Object.keys(attributes).length === 0) return null;
  return (
    <div className="mt-1.5 flex flex-wrap gap-1.5">
      {Object.entries(attributes).map(([k, v]) => {
        // Booleans that are false add no signal — skip them. A `true` boolean
        // renders as just the label chip (e.g. "in-house catering ✓").
        if (typeof v === "boolean" && v === false) return null;
        const label = prettyAttrKey(k);
        const isTrueFlag = typeof v === "boolean" && v === true;
        return (
          <span
            key={k}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-stone-100 text-[10px] text-stone-700 max-w-full"
            title={`${k}: ${formatAttrValue(v)}`}
          >
            <span className="text-stone-500">{label}{isTrueFlag ? "" : ":"}</span>
            {isTrueFlag ? <span className="text-emerald-700">✓</span> : (
              <span className="truncate max-w-[16rem]">{formatAttrValue(v)}</span>
            )}
          </span>
        );
      })}
    </div>
  );
}

interface StoreSummary {
  items_in?: number;
  items_stored?: number;
  items_with_price?: number;
  images_stored?: number;
}

function getStoreSummary(events: AgentEvent[]): StoreSummary | null {
  const storeEv = events.find((e) => e.step === "store");
  if (!storeEv) return null;
  const parsed = parseSummary(storeEv.output_summary);
  return parsed as StoreSummary | null;
}

/** One offering item card: title + price + per-pack attributes + its linked
 *  images. Extracted so each category cluster can reuse it. */
function ItemCard({ item: it, images: imgs }: { item: VendorDetail["items"][number]; images: VendorDetail["images"] }) {
  return (
    <div className="border border-stone-200 rounded-lg p-3 bg-white">
      <div className="flex justify-between gap-3 text-sm text-stone-700">
        <span>
          {it.title}{" "}
          <span className="text-stone-500 text-xs">
            ({it.category}{it.flower_material ? ` · ${it.flower_material}` : ""})
          </span>
        </span>
        <span className="font-mono text-stone-600 shrink-0">
          {it.price_cents == null ? "—" : `$${(it.price_cents / 100).toFixed(0)} ${it.currency}`}
        </span>
      </div>
      {it.description && (
        <p className="mt-1 text-xs text-stone-500 line-clamp-2">{it.description}</p>
      )}
      <ItemAttributes attributes={it.attributes} />
      {imgs.length > 0 ? (
        <div className="mt-2.5">
          <div className="text-[10px] uppercase tracking-wide text-stone-400 mb-1">
            {imgs.length} linked image{imgs.length === 1 ? "" : "s"}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {imgs.map((img) => (
              <VendorImageCard key={img.id} img={img} hideItemLabel />
            ))}
          </div>
        </div>
      ) : (
        <div className="mt-2 text-[11px] text-stone-400 italic">no linked images</div>
      )}
    </div>
  );
}

const isPriced = (it: VendorDetail["items"][number]): boolean =>
  it.price_cents != null && it.price_cents > 0;

function VendorPanel({ vendor, events }: { vendor: VendorDetail; events: AgentEvent[] }) {
  const store = getStoreSummary(events) ?? {};

  // Group images under the item they were linked to (vendor_image.vendor_item_id,
  // set by the link-images-to-items step). Images with no link — or a link to an
  // item that didn't persist — are "vendor-level" (generic portfolio shots).
  const itemIds = new Set(vendor.items.map((it) => it.id));
  const imagesByItem = new Map<string, VendorDetail["images"]>();
  const vendorLevelImages: VendorDetail["images"] = [];
  for (const img of vendor.images) {
    if (img.vendor_item_id && itemIds.has(img.vendor_item_id)) {
      const list = imagesByItem.get(img.vendor_item_id) ?? [];
      list.push(img);
      imagesByItem.set(img.vendor_item_id, list);
    } else {
      vendorLevelImages.push(img);
    }
  }
  const linkedImageCount = vendor.images.length - vendorLevelImages.length;

  // Cluster the vendor BY CATEGORY so a mixed-purpose vendor (a venue that also
  // caters) reads as separate offerings instead of one flat list + one
  // undifferentiated image bucket. Each cluster groups its items (titles +
  // prices + linked images) with the vendor-level images the vision step tagged
  // with that `matched_category`. This segments e.g. food (catering) from the
  // venue: a Park Lane–style venue shows a "venue" cluster (rooms, spaces) and a
  // "catering" cluster (the priced menu items + the dining/food photos) side by
  // side. Categories render primary-first.
  const categoryOrder = [...vendor.categories]
    .sort((a, b) => Number(b.is_primary) - Number(a.is_primary))
    .map((c) => c.slug);
  const knownCats = new Set(categoryOrder);

  const itemsByCategory = new Map<string, VendorDetail["items"]>();
  for (const it of vendor.items) {
    const list = itemsByCategory.get(it.category) ?? [];
    list.push(it);
    itemsByCategory.set(it.category, list);
  }
  // Unlinked vendor-level images bucketed by matched_category. Anything whose
  // matched_category isn't one of the vendor's categories (or is null) is
  // "unclassified" — the vision step couldn't tie it to an offering.
  const unlinkedByCategory = new Map<string, VendorDetail["images"]>();
  const unclassifiedImages: VendorDetail["images"] = [];
  for (const img of vendorLevelImages) {
    const cat = img.matched_category;
    if (cat && knownCats.has(cat)) {
      const list = unlinkedByCategory.get(cat) ?? [];
      list.push(img);
      unlinkedByCategory.set(cat, list);
    } else {
      unclassifiedImages.push(img);
    }
  }
  // Render a cluster for EVERY vendor category (primary-first), even one with no
  // items or images — a venue classified `catering` (in-house catering, no
  // published menu) must still surface as a catering cluster so the operator
  // sees the classification instead of it silently vanishing. Then fold in any
  // item category or image matched_category that isn't a registered vendor
  // category (defensive): those only render when they actually carry items or
  // images.
  const clusterCats = [
    ...categoryOrder,
    ...[...itemsByCategory.keys()].filter((c) => !knownCats.has(c)),
    ...[...unlinkedByCategory.keys()].filter((c) => !knownCats.has(c)),
  ].filter(
    (c, i, arr) =>
      arr.indexOf(c) === i &&
      (knownCats.has(c) ||
        (itemsByCategory.get(c)?.length ?? 0) > 0 ||
        (unlinkedByCategory.get(c)?.length ?? 0) > 0),
  );

  return (
    <div className="mt-2 border border-stone-200 rounded-lg p-5 bg-white">
      <header className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-2xl">{vendor.name}</h3>
          <div className="mt-1 text-sm text-stone-600 flex items-center gap-2">
            <span className="px-2 py-0.5 rounded-full bg-stone-100 text-xs">{vendor.source}</span>
            <span className="px-2 py-0.5 rounded-full bg-stone-100 text-xs">{vendor.status}</span>
            {vendor.ai_generated && <span className="px-2 py-0.5 rounded-full bg-violet-100 text-violet-800 text-xs">AI-generated</span>}
            {vendor.website && (
              <a href={vendor.website} target="_blank" rel="noreferrer" className="text-stone-500 hover:text-stone-900 underline">
                {vendor.website.replace(/^https?:\/\//, "")}
              </a>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="flex items-start gap-4">
            <Stat label="rating" value={typeof vendor.rating === "number" ? vendor.rating.toFixed(1) : "—"} />
            <Stat label="reviews" value={typeof vendor.review_count === "number" ? vendor.review_count.toLocaleString() : "—"} />
            <Stat label="quality" value={typeof vendor.quality_score === "number" ? (vendor.quality_score * 100).toFixed(0) : "—"} />
          </div>
          {vendor.rating_updated_at_utc_ms != null && (
            <div className="mt-1 text-[10px] text-stone-500">updated {formatRelativeAge(vendor.rating_updated_at_utc_ms)}</div>
          )}
        </div>
      </header>

      {vendor.description && (
        <p className="text-sm text-stone-700 mb-4">{vendor.description}</p>
      )}

      <section className="mb-5">
        <h4 className="text-xs uppercase tracking-wide text-stone-500 mb-2">categories ({vendor.categories.length})</h4>
        <div className="flex flex-wrap gap-2">
          {vendor.categories.map((c) => (
            <span key={c.slug} className={`px-3 py-1 rounded-full text-xs flex items-center gap-1.5 ${c.is_primary ? "bg-stone-900 text-stone-50" : "bg-stone-100"}`}>
              {c.is_primary && <span>★</span>}
              <span>{c.slug}</span>
              {c.confidence != null && <span className="opacity-60">{(c.confidence * 100).toFixed(0)}%</span>}
            </span>
          ))}
        </div>
      </section>

      <section className="mb-5">
        <h4 className="text-xs uppercase tracking-wide text-stone-500 mb-2">location</h4>
        {vendor.location ? (
          <div className="text-sm text-stone-700">
            <div>{vendor.location.address}</div>
            <div className="text-stone-500 text-xs mt-0.5">
              {[vendor.location.city, vendor.location.region, vendor.location.country].filter(Boolean).join(", ")}
              {vendor.location.lat != null && vendor.location.lng != null && (
                <span className="font-mono ml-2">({vendor.location.lat.toFixed(4)}, {vendor.location.lng.toFixed(4)})</span>
              )}
            </div>
          </div>
        ) : (
          <div className="text-sm text-stone-500 italic">no location (geocode skipped or failed)</div>
        )}
      </section>

      {/* Offerings clustered BY CATEGORY. Each cluster groups its items (titles
          + prices + linked images) with the unlinked vendor-level images the
          vision step tagged with that matched_category — so a venue that also
          caters shows its food (catering) segmented from its rooms (venue). */}
      {clusterCats.length > 0 && (
        <section className="mb-5">
          <h4 className="text-xs uppercase tracking-wide text-stone-500 mb-3 flex items-center gap-2">
            <span>offerings ({vendor.items.length} items, {clusterCats.length} {clusterCats.length === 1 ? "category" : "categories"})</span>
            {typeof store.items_with_price === "number" && typeof store.items_in === "number" && (
              <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 text-[10px] font-mono normal-case tracking-normal">
                {store.items_with_price}/{store.items_in} priced
              </span>
            )}
            <span className="px-2 py-0.5 rounded-full bg-sky-50 text-sky-800 text-[10px] font-mono normal-case tracking-normal">
              {linkedImageCount} item-linked images
            </span>
          </h4>
          <div className="space-y-5">
            {clusterCats.map((cat) => {
              const catItems = itemsByCategory.get(cat) ?? [];
              const catImages = unlinkedByCategory.get(cat) ?? [];
              const meta = vendor.categories.find((c) => c.slug === cat);
              const priced = catItems.filter(isPriced).length;
              return (
                <div key={cat} className="border border-stone-200 rounded-lg p-4 bg-stone-50/40">
                  <div className="mb-3 flex flex-wrap items-baseline gap-2">
                    {meta?.is_primary && <span className="text-stone-900">★</span>}
                    <span className="font-display text-lg capitalize">{cat}</span>
                    {meta?.confidence != null && (
                      <span className="text-[10px] text-stone-400 font-mono">
                        {(meta.confidence * 100).toFixed(0)}% conf
                      </span>
                    )}
                    {!meta && (
                      <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px]">
                        not a vendor category
                      </span>
                    )}
                    <span className="text-xs text-stone-500">
                      {catItems.length} item{catItems.length === 1 ? "" : "s"}
                    </span>
                    {catItems.length > 0 && (
                      <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 text-[10px] font-mono">
                        {priced}/{catItems.length} priced
                      </span>
                    )}
                    {catImages.length > 0 && (
                      <span className="px-2 py-0.5 rounded-full bg-sky-50 text-sky-800 text-[10px] font-mono">
                        {catImages.length} gallery image{catImages.length === 1 ? "" : "s"}
                      </span>
                    )}
                  </div>

                  {catItems.length === 0 && catImages.length === 0 && (
                    <div className="text-[11px] text-stone-400 italic">
                      classified as {cat}, but no offerings or images were extracted for it
                      {meta && !meta.is_primary ? " (secondary category — e.g. in-house catering with no published menu)" : ""}.
                    </div>
                  )}

                  {catItems.length > 0 && (
                    <div className="space-y-2.5">
                      {catItems.map((it) => (
                        <ItemCard key={it.id} item={it} images={imagesByItem.get(it.id) ?? []} />
                      ))}
                    </div>
                  )}

                  {catImages.length > 0 && (
                    <div className="mt-3">
                      <div className="text-[10px] uppercase tracking-wide text-stone-400 mb-1.5">
                        {cat} gallery — {catImages.length} image{catImages.length === 1 ? "" : "s"} not tied to a specific item
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {catImages.map((img) => (
                          <VendorImageCard key={img.id} img={img} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Images the vision step couldn't tie to any of the vendor's categories
          (matched_category null or off-taxonomy). */}
      <section>
        <h4 className="text-xs uppercase tracking-wide text-stone-500 mb-2 flex items-center gap-2">
          <span>
            {clusterCats.length > 0 ? "unclassified images" : "images"} ({unclassifiedImages.length}
            {clusterCats.length > 0 ? "" : " approved"})
          </span>
          {typeof store.images_stored === "number" && store.images_stored !== vendor.images.length && (
            <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 text-[10px] font-mono normal-case tracking-normal">
              ⚠ store wrote {store.images_stored} of {vendor.images.length}
            </span>
          )}
        </h4>
        {unclassifiedImages.length === 0 ? (
          <div className="text-sm text-stone-500 italic">
            {vendor.images.length === 0
              ? "no approved images"
              : "every approved image is clustered under an offering above"}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {unclassifiedImages.map((img) => (
              <VendorImageCard key={img.id} img={img} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/**
 * Coloured pill for vendor_image.image_type. Only the four labels that
 * carry signal for images get a pill — portfolio (violet), venue_spaces
 * (amber), products (slate), testimonials (rose). Everything else (home,
 * about, contact, hero, etc.) renders no pill.
 */
function imageTypePill(t: string | null) {
  if (!t) return null;
  const style: Record<string, string> = {
    portfolio: "bg-violet-100 text-violet-800",
    venue_spaces: "bg-amber-100 text-amber-800",
    products: "bg-slate-200 text-slate-800",
    testimonials: "bg-rose-100 text-rose-800",
    floor_plan: "bg-teal-100 text-teal-800",
    site_map: "bg-teal-100 text-teal-800",
  };
  const cls = style[t];
  if (!cls) return null;
  return <span className={`px-1.5 py-0.5 rounded ${cls}`}>{t}</span>;
}

function VendorImageCard({ img, hideItemLabel }: { img: VendorDetail["images"][number]; hideItemLabel?: boolean }) {
  const [open, setOpen] = useState(false);
  const hasDescription = (img.description_long?.length ?? 0) > 0;
  const formattedPrice =
    img.item_price_cents != null
      ? `$${(img.item_price_cents / 100).toFixed(0)} ${img.item_currency ?? "USD"}`
      : null;
  return (
    <div className="rounded-lg overflow-hidden border border-stone-200 bg-stone-50">
      <AuthedImage src={`${API_BASE()}/admin/image/${img.id}`} alt={img.matched_category ?? "vendor image"} />
      {!hideItemLabel && img.item_title && (
        <div className="px-2 pt-2 text-[11px] flex items-baseline justify-between gap-2">
          <span className="font-medium text-stone-800 truncate" title={img.item_title}>
            {img.item_title}
          </span>
          {formattedPrice && (
            <span className="font-mono text-stone-600 shrink-0">{formattedPrice}</span>
          )}
        </div>
      )}
      <div className="p-2 text-[11px] text-stone-600 flex flex-wrap gap-1.5">
        {img.matched_category && <span className="px-1.5 py-0.5 rounded bg-stone-200">{img.matched_category}</span>}
        {imageTypePill(img.image_type)}
        {img.quality_score != null && <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">q {(img.quality_score * 100).toFixed(0)}</span>}
        {img.relevance_score != null && <span className="px-1.5 py-0.5 rounded bg-sky-100 text-sky-800">r {(img.relevance_score * 100).toFixed(0)}</span>}
        {img.flower_species && img.flower_species.length > 0 && img.flower_species.slice(0, 4).map((s) => (
          <span key={s} className="px-1.5 py-0.5 rounded bg-pink-50 text-pink-800">{s}</span>
        ))}
      </div>
      {hasDescription && (
        <button
          onClick={() => setOpen((v) => !v)}
          className="w-full px-2 py-1 text-[10px] text-stone-500 hover:text-stone-800 hover:bg-stone-100 text-left border-t border-stone-200"
        >
          {open ? "▾ hide description" : "▸ show description"}
        </button>
      )}
      {open && hasDescription && (
        <div className="px-2 py-2 text-[11px] leading-relaxed text-stone-700 bg-white border-t border-stone-200 max-h-48 overflow-y-auto">
          {img.description_long}
        </div>
      )}
    </div>
  );
}

// --- Authed image (browsers can't put headers on <img src>) -----------------

function AuthedImage({ src, alt }: { src: string; alt: string }) {
  const { getToken } = useAuth();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    let revoke: string | null = null;
    (async () => {
      const token = await getToken();
      if (!token || cancelled) return;
      const res = await fetch(src, { headers: { authorization: `Bearer ${token}` } });
      if (!res.ok || cancelled) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      revoke = url;
      if (!cancelled) setBlobUrl(url);
    })();
    return () => { cancelled = true; if (revoke) URL.revokeObjectURL(revoke); };
  }, [src, getToken]);
  if (!blobUrl) return <div className="aspect-square bg-stone-100 flex items-center justify-center text-stone-400 text-xs">loading…</div>;
  return <img src={blobUrl} alt={alt} className="w-full aspect-square object-cover" />;
}
